import { useEffect, useRef, useState } from 'react'
import { tiempoConcluido } from '@domino/core'
import type { MiTorneo } from '../datos/index.js'

/**
 * La pausa entre rondas, en los torneos sin organizador que la tienen.
 *
 * Cuenta hacia atrás contra `proximaRondaEn`, que manda el API, igual que el
 * reloj de la ronda: el teléfono solo muestra, y quien sortea la ronda al
 * llegar a cero es el servidor. La ronda nueva llega por los avisos del torneo
 * como cualquier otra.
 *
 * Va arriba de las pestañas y no adentro de «Mi mesa»: el que está mirando la
 * tabla durante la pausa es justo el que tiene que enterarse de que se termina.
 *
 * Además del cartel, avisa: el teléfono vibra cuando empieza la pausa y cuando
 * se sortea la ronda, y si el jugador lo pidió (el botón del cartel) y tiene la
 * pantalla en otra cosa, le llega una notificación del sistema.
 */

function mmss(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000))
  const minutos = Math.floor(total / 60)
  const segundos = total % 60
  return `${minutos}:${String(segundos).padStart(2, '0')}`
}

/** Si este navegador sabe mostrar notificaciones del sistema. */
function hayNotificaciones(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window
}

/**
 * Vibra y, si hay permiso y la pestaña no está a la vista, notifica. Con la
 * pestaña a la vista alcanza con el cartel: una notificación encima de lo que
 * ya se está leyendo es ruido.
 */
function avisar(titulo: string, cuerpo: string, etiqueta: string): void {
  try {
    navigator.vibrate?.([200, 100, 200])
  } catch {
    // Hay navegadores que la tienen y la niegan; el cartel ya avisa igual.
  }
  if (!hayNotificaciones() || Notification.permission !== 'granted') return
  if (document.visibilityState === 'visible') return
  try {
    new Notification(titulo, { body: cuerpo, tag: etiqueta })
  } catch {
    // En Android el constructor no existe fuera de un service worker.
  }
}

export function PausaEntreRondas({ torneo }: { torneo: MiTorneo }) {
  const { proximaRondaEn, rondaEnCurso, rondasCerradas } = torneo
  const siguiente = rondasCerradas + 1

  /* Los avisos salen al CAMBIAR, no al abrir la pantalla: el que entra a mitad
     de la pausa ya la está viendo. La primera vuelta solo anota cómo estaba. */
  const antes = useRef<{ pausa: string | null; ronda: number | null } | null>(null)
  useEffect(() => {
    const previo = antes.current
    antes.current = { pausa: proximaRondaEn, ronda: rondaEnCurso }
    if (previo === null) return

    if (proximaRondaEn !== null && previo.pausa === null) {
      avisar(
        `${torneo.nombre}: pausa entre rondas`,
        `La ronda ${siguiente} se sortea en ${mmss(new Date(proximaRondaEn).getTime() - Date.now())}.`,
        `pausa-${torneo.id}`,
      )
    } else if (rondaEnCurso !== null && rondaEnCurso !== previo.ronda) {
      avisar(
        `${torneo.nombre}: ronda ${rondaEnCurso}`,
        `Ya se sorteó la ronda ${rondaEnCurso}. Buscá tu mesa.`,
        `ronda-${torneo.id}`,
      )
    }
  }, [proximaRondaEn, rondaEnCurso, siguiente, torneo.id, torneo.nombre])

  const [ahora, setAhora] = useState(() => Date.now())
  const concluida = proximaRondaEn !== null && tiempoConcluido(proximaRondaEn, ahora)

  useEffect(() => {
    if (proximaRondaEn === null || concluida) return
    const tic = window.setInterval(() => setAhora(Date.now()), 1000)
    return () => window.clearInterval(tic)
  }, [proximaRondaEn, concluida])

  const [permiso, setPermiso] = useState(() =>
    hayNotificaciones() ? Notification.permission : 'denied',
  )

  if (proximaRondaEn === null || torneo.estado !== 'en_curso') return null

  if (concluida) {
    return (
      <div className="reloj-ronda pausa" role="status">
        <span>Sorteando la ronda {siguiente}…</span>
      </div>
    )
  }

  return (
    <div className="reloj-ronda pausa" role="timer">
      <span>Pausa entre rondas: la ronda {siguiente} se sortea en</span>
      <strong>{mmss(new Date(proximaRondaEn).getTime() - ahora)}</strong>
      {/* Se pide al tocar y no al entrar: un navegador que pregunta sin que
          nadie haya hecho nada es un «no» seguro, y ese no se puede deshacer. */}
      {permiso === 'default' && (
        <button
          className="pausa-avisarme"
          onClick={() => void Notification.requestPermission().then(setPermiso)}
        >
          Avisarme cuando empiece
        </button>
      )}
    </div>
  )
}
